import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import useConsumeLocationState from '@/shared/hooks/useConsumeLocationState';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useTerm } from '@/shared/contexts/TermContext';
import useMyCandidacies from '@/shared/hooks/useMyCandidacies';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import studentApplicationService from '@/shared/services/studentApplicationService';
import {
    PROFILE_APPLYING_STATUSES,
    candidaciesForProfile,
} from '@/shared/utils/candidacyStage';
import { PROGRAM_NAMES } from '@/shared/utils/deadline';
import { positionCourse } from '@/shared/utils/positionText';
import { withdrawEffects } from '@/shared/utils/studentPositions';
import StatusPill from '@/components/common/StatusPill';
import PositionApplicationsList from './PositionApplicationsList';
import StudentApplicationAnswers from './StudentApplicationAnswers';
import NewerResumeOffer from './NewerResumeOffer';
import useAcceptInfo from './useAcceptInfo';
import WithdrawConfirmModal from './WithdrawConfirmModal';
import { termLabel } from '@/shared/utils/termLabel';

// The statuses the server lets the owner withdraw from, less draft: a draft
// is continued, not withdrawn.
const WITHDRAWABLE_STATUSES = [
    'submitted',
    'under_review',
    'interview_scheduled',
    'rejected',
    'declined',
];
const EDITABLE_STATUSES = ['draft', 'submitted'];

// The owner's read answers `student` as an id, or as a populated user.
const studentIdOf = (application) =>
    String(application.student?._id ?? application.student);

function NotFound() {
    return (
        <Container className="py-4">
            <h1 className="h3">Application not found</h1>
            <p>We couldn&apos;t find that application.</p>
            <Link to="/applications">Back to your applications</Link>
        </Container>
    );
}

// Names carry their own commas ("CHE 1810, section 003"), so they separate
// with semicolons.
const joinNames = (names) =>
    names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join('; ')} and ${names[names.length - 1]}`;

function withdrawBody({ programName, termLabel, affected, kept, offered }) {
    if (kept.length > 0) {
        return (
            <p className="mb-0">
                {`Your accepted ${kept.length === 1 ? 'position' : 'positions'}, ${joinNames(kept)}, ${kept.length === 1 ? 'is' : 'are'} not affected. But this application can't be withdrawn while you hold ${kept.length === 1 ? 'it' : 'them'}. Leave ${kept.length === 1 ? 'that position' : 'those positions'} first, then withdraw.`}
            </p>
        );
    }
    const lines = [];
    if (affected.length > 0) {
        const including = offered
            ? ", including an offer you haven't answered"
            : '';
        lines.push(
            `This also withdraws your ${affected.length === 1 ? 'application' : 'applications'} to ${joinNames(affected)}${including}.`
        );
    }
    return (
        <>
            <p className={lines.length ? 'mb-2' : 'mb-0'}>
                {`You can't start another ${programName} application for ${termLabel}.`}
            </p>
            {lines.map((line) => (
                <p key={line} className="mb-0">
                    {line}
                </p>
            ))}
        </>
    );
}

/**
 * A student's own application: their answers, each position they applied to
 * with its stage, and the means to withdraw from one position or from the
 * whole application. Only the owner sees it; anyone else gets the page an
 * unknown id gets.
 */
function MyStudentApplicationPage() {
    const { id } = useParams();
    const { userProfile } = useAuth();
    const { currentTerm } = useTerm();
    const previewLock = usePreviewLock();
    const candidacyState = useMyCandidacies();
    // notice: set by /apply when the student already has this application.
    const arrival = useConsumeLocationState(['notice']);
    const [notice, setNotice] = useState(arrival.notice || null);
    const { candidacies, commitments, replace, reload } = candidacyState;
    const [state, setState] = useState({
        status: 'loading',
        application: null,
    });
    const [dialog, setDialog] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const [focusHeading, setFocusHeading] = useState(false);
    const [focusStatus, setFocusStatus] = useState(false);
    const headingRef = useRef(null);
    const statusRef = useRef(null);
    const requestRef = useRef(0);
    const acceptInfo = useAcceptInfo(
        state.application,
        candidaciesForProfile(candidacies, state.application || {}).some((c) =>
            ['offered', 'accepted'].includes(c.stage)
        )
    );

    const load = useCallback(async () => {
        requestRef.current += 1;
        const request = requestRef.current;
        try {
            const application =
                await studentApplicationService.getApplicationById(id);
            if (request === requestRef.current) {
                setState({ status: 'ready', application });
            }
        } catch (err) {
            if (request !== requestRef.current) return;
            const code = err?.response?.status;
            setState({
                status: code === 403 || code === 404 ? 'notFound' : 'error',
                application: null,
            });
        }
    }, [id]);

    useEffect(() => {
        setState({ status: 'loading', application: null });
        load();
        return () => {
            requestRef.current += 1;
        };
    }, [load]);

    useEffect(() => {
        if (focusHeading && headingRef.current) {
            headingRef.current.focus();
            setFocusHeading(false);
        }
    }, [focusHeading, state]);

    // The offer card unmounts when the student takes it, so focus moves to the
    // status that says what changed.
    useEffect(() => {
        if (focusStatus && statusRef.current) {
            statusRef.current.focus();
            setFocusStatus(false);
        }
    }, [focusStatus, announcement]);

    if (state.status === 'loading') {
        return (
            <Container className="py-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">
                        Loading your application
                    </span>
                </Spinner>
            </Container>
        );
    }
    if (state.status === 'error') {
        return (
            <Container className="py-4">
                <Alert variant="danger">
                    Your application couldn&apos;t be loaded.
                </Alert>
                <Button variant="outline-primary" onClick={load}>
                    Try again
                </Button>
            </Container>
        );
    }

    const { application } = state;
    if (
        state.status === 'notFound' ||
        !userProfile?._id ||
        studentIdOf(application) !== String(userProfile._id)
    ) {
        return <NotFound />;
    }

    const programName =
        PROGRAM_NAMES[application.program] || application.program;
    const termName = termLabel(currentTerm, application.term);
    const forProfile = candidaciesForProfile(candidacies, application);
    const positionLabels = Object.fromEntries(
        forProfile
            .filter((c) => c.position)
            .map((c) => [String(c.applicationId), positionCourse(c.position)])
    );
    const canApply = PROFILE_APPLYING_STATUSES.includes(application.status);

    // The section renders in one of two slots. Its key lets React move it
    // between them rather than remount it: answering the last offer flips
    // `offerFirst`, and a remount would drop the offer card's result before
    // it takes focus.
    const offerFirst = forProfile.some((c) => c.stage === 'offered');
    const positionsSection = (
        <section
            key="positions"
            aria-labelledby="my-positions-heading"
            className="mb-4"
        >
            <h2 id="my-positions-heading" className="h4">
                Positions you applied to
            </h2>
            {candidacyState.loading ? (
                <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">
                        Loading your position applications
                    </span>
                </Spinner>
            ) : candidacyState.error ? (
                <>
                    <Alert variant="warning">
                        Your position applications couldn&apos;t be loaded.
                    </Alert>
                    <Button variant="outline-primary" onClick={reload}>
                        Try again
                    </Button>
                </>
            ) : forProfile.length > 0 ? (
                <PositionApplicationsList
                    candidacies={forProfile}
                    commitments={commitments}
                    variant="full"
                    onChange={replace}
                    onReload={reload}
                    locked={previewLock}
                    acceptInfo={acceptInfo}
                />
            ) : (
                <p>
                    You haven&apos;t applied to any positions yet.
                    {canApply && (
                        <>
                            {' '}
                            <Link to="/positions">See open positions</Link>
                        </>
                    )}
                </p>
            )}
        </section>
    );

    const openWithdraw = () =>
        setDialog({
            ...withdrawEffects(forProfile),
            offered: forProfile.some((c) => c.stage === 'offered'),
        });

    const confirmWithdraw = async () => {
        await studentApplicationService.withdrawApplication(id);
        setDialog(null);
        await load();
        reload();
        setAnnouncement('Your application was withdrawn.');
        setFocusHeading(true);
    };

    return (
        <Container className="py-4">
            <h1 className="h2" tabIndex={-1} ref={headingRef}>
                Your {programName} application
            </h1>
            <p className="mb-3">
                {termName}{' '}
                <StatusPill
                    family="studentApplication"
                    value={application.status}
                />
            </p>
            {notice && (
                <Alert
                    variant="info"
                    dismissible
                    onClose={() => setNotice(null)}
                >
                    {notice}
                </Alert>
            )}
            <div role="status" tabIndex={-1} ref={statusRef}>
                {announcement && (
                    <Alert variant="success" className="mb-3">
                        {announcement}
                    </Alert>
                )}
            </div>

            {offerFirst && positionsSection}

            <div className="d-flex flex-wrap gap-2 mb-2">
                {EDITABLE_STATUSES.includes(application.status) && (
                    <Button
                        as={Link}
                        to={`/apply/${application._id}/edit`}
                        variant="outline-primary"
                    >
                        Edit application
                    </Button>
                )}
                {WITHDRAWABLE_STATUSES.includes(application.status) && (
                    <Button
                        variant="outline-danger"
                        disabled={previewLock}
                        aria-describedby={
                            previewLock ? PREVIEW_LOCK_NOTE_ID : undefined
                        }
                        onClick={openWithdraw}
                    >
                        Withdraw application
                    </Button>
                )}
            </div>
            <div className="mb-4">
                <PreviewLockNote locked={previewLock} />
            </div>

            {!offerFirst && positionsSection}

            <section aria-labelledby="my-answers-heading">
                <h2 id="my-answers-heading" className="h4">
                    Your answers
                </h2>
                <NewerResumeOffer
                    application={application}
                    profileResume={userProfile?.resume}
                    onUpdated={(updated) => {
                        setState({ status: 'ready', application: updated });
                        setAnnouncement(
                            'This application now uses the newer resume from your profile.'
                        );
                        setFocusStatus(true);
                    }}
                />
                <StudentApplicationAnswers
                    application={application}
                    positionLabels={positionLabels}
                />
            </section>

            <WithdrawConfirmModal
                show={Boolean(dialog)}
                title={`Withdraw your ${programName} application?`}
                body={
                    dialog
                        ? withdrawBody({
                              programName,
                              termLabel: termName,
                              affected: dialog.affected,
                              kept: dialog.kept,
                              offered: dialog.offered,
                          })
                        : null
                }
                confirmLabel="Withdraw application"
                confirmDisabled={Boolean(dialog?.kept.length)}
                onConfirm={confirmWithdraw}
                onCancel={() => setDialog(null)}
            />
        </Container>
    );
}

export default MyStudentApplicationPage;
